import type { EventWebsiteGuestQuery } from '../../GuestSite.graphql.generated';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { EventPageVisibility } from '@graphql/generated-base';
import { DesignLayoutType } from '@graphql/generated-base';
import { useEffect, useMemo, useCallback } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { getURLParams } from '@shared/utils/urlHelpers';
import type { SearchParams } from './GuestSite.types';
import { useFeatureValue } from '@shared/core/featureFlags';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { isInIframe } from '@shared/utils/isInIframe';
import type { EnabledPagesInfo, GuestSiteLoadTelemetry, SecurityInfo } from '@apps/guest/GuestSite.telemetry';
import { differenceInCalendarDays } from 'date-fns';
import type { ExtendedEventUserStatus } from '@shared/components/AuthProvider/AuthProvider.utils';
import { useGuestSiteQueryParams } from '@apps/guest/packages/layout-engine/common/utils/useGuestSiteQueryParams';

export { shouldUseEventDesignDraft } from '@shared/utils/eventDesignPurpose';

export const useReloadForLegacyGuestsite = (
  eventFeatureFlags: GuestSiteEvent['featureFlags'],
  website: Maybe<string>,
  useLegacyGuestSite: GuestSiteEvent['useLegacyGuestSite'],
) => {
  const useCharmFeatureFlagFromTable = eventFeatureFlags && eventFeatureFlags.includes('useCharm');
  const useLegacyGuestSiteFeatureFlagFromTable = eventFeatureFlags && eventFeatureFlags.includes('useLegacyGuestSite');
  const isCharm = useFeatureValue('guestsitetarget') === 'charm';
  const noSsr = useFeatureValue('nossr');
  /**
   * If the event has 'useCharm' defined in the feature_flags table then
   * - proceed to CHARM guest site (do not reload)
   * If the event does not have 'useCharm'
   * - proceed to LEGACY guest site if the event has 'useLegacyGuestSite' defined in the feature_flags table (reload)
   * - proceed to LEGACY guest site if the event's useLegacyGuestSite query returns true
   */
  useEffect(() => {
    if (!useCharmFeatureFlagFromTable && (useLegacyGuestSiteFeatureFlagFromTable || useLegacyGuestSite)) {
      withWindow(window => {
        const params = getURLParams<SearchParams>(window.location.search);
        if (
          params.ctx === 'adminGuestSitePreview' ||
          params.preview === 'true' ||
          params.target === 'charm' ||
          noSsr ||
          /**
           * assume that if you are in an iframe you should be rendering in a preview
           * state and you should therefore always render the guest app instead of app-browser
           */
          isInIframe() ||
          isCharm ||
          // special events passing through to charm guest site
          website === 'richardtait'
        ) {
          return;
        } else {
          // render the app-browser site by reloading the page allowing charm server to kick the user to nginx
          window.location.reload();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCharm, noSsr, eventFeatureFlags]);
};

interface Pages extends Omit<EventPageFragment, 'id' | 'type' | 'isPageContainer'> {
  visibility: EventPageVisibility;
  private: boolean;
}

export const useGenerateGuestSiteLoadTelemData = (data: EventWebsiteGuestQuery['eventByName'], role: ExtendedEventUserStatus): GuestSiteLoadTelemetry => {
  const telemPages = data?.pages;
  const queryParams = useGuestSiteQueryParams();

  const useEnabledPages = useCallback((pages?: Pages[]): EnabledPagesInfo[] => {
    const pagesEnabled: EnabledPagesInfo[] = [];
    pages?.forEach(page => {
      if (page?.disabled === false) {
        pagesEnabled.push({ slug: page.pageSlug, title: page.pageTitle, visibility: page.visibility });
      }
    });
    return pagesEnabled;
  }, []);

  const useEventSecurityStatus = useCallback((pages?: Pages[], hasUnlocked?: boolean): SecurityInfo => {
    const passwordProtectedPages = [];
    pages?.forEach(page => {
      if (page?.private) {
        passwordProtectedPages.push(page);
      }
    });

    const requiresPassword = passwordProtectedPages.length > 0 ? true : false;
    const isUnlocked = passwordProtectedPages.length === 0 ? undefined : hasUnlocked;
    return { requiresPassword, isUnlocked };
  }, []);

  // existing isAdmin conflates superAdmin and admin role, for telem we specifically care about the distinction
  const getRole = (role: ExtendedEventUserStatus) => {
    if (role == 'superAdmin') {
      return 'super admin';
    } else if (role === 'admin') {
      return 'admin';
    } else {
      return 'guest';
    }
  };

  const useDaysUntilEvent = (finalizedEventDate: string | undefined): number | undefined => {
    const daysUntilEvent = finalizedEventDate ? differenceInCalendarDays(new Date(finalizedEventDate), new Date()) : undefined;
    return daysUntilEvent;
  };

  const enabledPages = useEnabledPages(telemPages);
  const { requiresPassword, isUnlocked } = useEventSecurityStatus(telemPages, data?.activeSession.hasUnlocked);
  const daysUntilEvent = useDaysUntilEvent(data?.info?.finalizedDate?.dateString) || undefined;
  const extraInfo = useMemo(() => {
    return {
      layout: data?.eventDesign?.websiteLayout.layoutType || DesignLayoutType.aloha,
      userRole: getRole(role),
      security: {
        requiresPassword,
        isUnlocked,
      },
      enabledPages: enabledPages,
      daysUntilEvent,
      ...queryParams,
    };
  }, [enabledPages, isUnlocked, requiresPassword, role, data?.eventDesign?.websiteLayout.layoutType, daysUntilEvent, queryParams]);

  return extraInfo;
};
